import {
  ArrowUpDown,
  Building2,
  CalendarPlus,
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  SearchX,
  SlidersHorizontal,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  EmptyState,
  Form,
  IconButton,
  Menu,
  MenuRadioGroup,
  Notice,
  TextField,
  type MenuRadioOption,
} from '@/components/play'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import type { EventResource } from '@/types/events'
import {
  eventFilterOptions,
  eventSortOptions,
} from '@/pages/dashboard/constants'
import type {
  EventFilter,
  EventPagination,
  EventSort,
} from '@/pages/dashboard/types'

type EventLibrarySectionProps = {
  events: EventResource[]
  pagination: EventPagination
  loading: boolean
  error: string | null
  eventSearchInput: string
  setEventSearchInput: (value: string) => void
  eventFilter: EventFilter
  eventSort: EventSort
  handleEventFilterChange: (value: string) => void
  handleEventSortChange: (value: string) => void
  clearEventFilters: () => void
  hasActiveEventFilters: boolean
  totalPages: number
  canGoPrevious: boolean
  canGoNext: boolean
  setShowNew: (open: boolean) => void
  /** Where Create event leads instead of the form: start hosting, for an account without an organization. */
  createTo?: string
  editingId: string | null
  editingName: string
  setEditingName: (name: string) => void
  setEditingId: (id: string | null) => void
  startEdit: (event: EventResource) => void
  saveEdit: (event: EventResource) => void
  deleteEvent: (event: EventResource) => void
  setEventPage: (updater: (page: number) => number) => void
  onRetry: () => void
}

export function EventLibrarySection(props: EventLibrarySectionProps) {
  const {
    events,
    pagination,
    loading,
    error,
    eventSearchInput,
    setEventSearchInput,
    eventFilter,
    eventSort,
    handleEventFilterChange,
    handleEventSortChange,
    clearEventFilters,
    hasActiveEventFilters,
    totalPages,
    canGoPrevious,
    canGoNext,
    setShowNew,
    createTo,
    editingId,
    editingName,
    setEditingName,
    setEditingId,
    startEdit,
    saveEdit,
    deleteEvent,
    setEventPage,
    onRetry,
  } = props
  const menus: Array<{
    label: string
    icon: LucideIcon
    value: string
    onChange: (value: string) => void
    options: readonly MenuRadioOption<string>[]
  }> = [
    {
      label: 'Filter events',
      icon: SlidersHorizontal,
      value: eventFilter,
      onChange: handleEventFilterChange,
      options: eventFilterOptions,
    },
    {
      label: 'Sort events',
      icon: ArrowUpDown,
      value: eventSort,
      onChange: handleEventSortChange,
      options: eventSortOptions,
    },
  ]
  return (
    <section aria-label="Event library">
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <TextField
          type="search"
          label="Search events"
          hideLabel
          icon={<Search />}
          value={eventSearchInput}
          onChange={(event) => setEventSearchInput(event.target.value)}
          placeholder="Find an event"
          className="min-w-0 flex-1"
        />
        {menus.map(({ label, icon: Icon, value, onChange, options }) => (
          <Menu
            key={label}
            align="end"
            trigger={
              <IconButton label={label} variant="ghost">
                <Icon />
              </IconButton>
            }
          >
            <MenuRadioGroup
              label={label}
              hideLabel
              items={options}
              value={value}
              onValueChange={onChange}
            />
          </Menu>
        ))}
        {createTo ? (
          <ButtonLink to={createTo}>
            <Plus aria-hidden="true" />
            Create event
          </ButtonLink>
        ) : (
          <Button onClick={() => setShowNew(true)}>
            <Plus aria-hidden="true" />
            Create event
          </Button>
        )}
      </div>
      {loading && (
        <ContentSkeleton label="Loading your events" variant="list" className="py-8" />
      )}
      {/* A polite status: the list failed to load, not something the person just did. */}
      {error && (
        <Notice
          tone="error"
          title="We couldn’t load your events"
          action={
            <Button variant="secondary" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              Try again
            </Button>
          }
        >
          {error}
        </Notice>
      )}
      {/* Create event already leads the library, so the first-use state doesn't repeat it. */}
      {!loading && !error && events.length === 0 && (
        hasActiveEventFilters ? (
          <EmptyState
            icon={<SearchX />}
            title="No matching events"
            description="Try another search or filter, or clear them to see every event."
            action={<Button variant="secondary" onClick={clearEventFilters}>Clear filters</Button>}
          />
        ) : (
          <EmptyState
            icon={<CalendarPlus />}
            title="Your first event starts here."
            description="Create an event, then invite cameras and share its public page."
          />
        )
      )}
      {!loading && !error && (
        <div className="play-event-list">
          {events.map((event) => {
            const name = event.attributes.name
            // Renaming swaps the card's name for its field; the card stops being a link until the rename ends.
            if (editingId === event.id) {
              return (
                <Card as="article" key={event.id} aria-label={`Rename ${name}`}>
                  {/* A Play Form, so an emptied name is said beside the field rather than in the browser's bubble. The
                      Form is a grid, so the row is its own element rather than utilities competing with that. */}
                  <Form onFormSubmit={() => saveEdit(event)}>
                    <div className="flex items-start gap-2">
                      <TextField
                        autoFocus
                        label="Event name"
                        hideLabel
                        className="min-w-0 flex-1"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        required
                        requiredMessage="Name your event."
                      />
                      <IconButton type="submit" label="Save event name" variant="ghost">
                        <Check />
                      </IconButton>
                      <IconButton
                        label="Cancel rename"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        <X />
                      </IconButton>
                    </div>
                  </Form>
                </Card>
              )
            }
            // An event managed for another organization: named for that organization, and only its owners delete it.
            const managed = event.attributes.access === 'manager'
            const owner = event.attributes.organization_name
            return (
              <Card
                as="article"
                key={event.id}
                title={name}
                description={
                  event.attributes.starts_at
                    ? new Date(event.attributes.starts_at).toLocaleDateString(undefined, { dateStyle: 'medium' })
                    : 'Date to come'
                }
                action={{ to: `/events/${event.id}`, label: `Open ${name}` }}
                actions={
                  <>
                    <IconButton label={`Rename ${name}`} variant="ghost" onClick={() => startEdit(event)}>
                      <Pencil />
                    </IconButton>
                    {!event.attributes.is_default && !managed && (
                      <IconButton label={`Delete ${name}`} variant="ghost" onClick={() => deleteEvent(event)}>
                        <Trash2 />
                      </IconButton>
                    )}
                  </>
                }
              >
                {managed ? (
                  <Badge tone="quiet" icon={<Building2 />}>
                    <span className="sr-only">Managed for </span>
                    {owner || 'Another organization'}
                  </Badge>
                ) : null}
              </Card>
            )
          })}
        </div>
      )}
      {!loading && !error && totalPages > 1 && (
        <nav
          aria-label="Event pages"
          className="mt-8 flex items-center justify-center gap-4"
        >
          <IconButton
            label="Previous event page"
            variant="ghost"
            disabled={!canGoPrevious}
            onClick={() => setEventPage((page) => Math.max(1, page - 1))}
          >
            <ChevronLeft />
          </IconButton>
          <span className="text-sm tabular-nums text-neutral-7">
            <span aria-hidden="true">{pagination.current_page} / {totalPages}</span>
            <span className="sr-only">Page {pagination.current_page} of {totalPages}</span>
          </span>
          <IconButton
            label="Next event page"
            variant="ghost"
            disabled={!canGoNext}
            onClick={() =>
              setEventPage((page) => Math.min(totalPages, page + 1))
            }
          >
            <ChevronRight />
          </IconButton>
        </nav>
      )}
    </section>
  )
}
